import React, { useEffect, useRef, useState } from "react";
import { TopNavbar } from "@/components/layout";
import { useTenant } from "@/hooks/useTenant";
import { TokenManager } from "@/supabase/supabaseClient";

export const Reporting: React.FC = () => {
  const dashboardRef = useRef<HTMLDivElement>(null);
  const [error, setError] = useState<string | null>(null);
  const { tenantId } = useTenant();

  useEffect(() => {
    if (!tenantId) return;

    async function embed() {
      try {
        // Dynamically import the superset embedded sdk to avoid Vite
        // dependency pre-bundling issues in development. This also
        // ensures the module is only loaded in the browser runtime.
        const { embedDashboard } = await import("@superset-ui/embedded-sdk");
        if (!dashboardRef.current) return;

        await embedDashboard({
          id: import.meta.env.VITE_SUPERSET_DASHBOARD_ID,
          supersetDomain: import.meta.env.VITE_SUPERSET_DOMAIN,
          mountPoint: dashboardRef.current,
          fetchGuestToken: async () => {
            try {
              // Use environment variable for API URL, fallback to relative URL for dev
              const baseUrl = import.meta.env.VITE_API_BASE_URL
                ? `${import.meta.env.VITE_API_BASE_URL}/api/superset/guest-token`
                : "/api/superset/guest-token";

              const apiUrl = `${baseUrl}?tenantId=${encodeURIComponent(tenantId!)}`;

              // Include auth token for tenant verification
              const token = (await TokenManager.getStoredTokenAsync()) || "";
              const headers: Record<string, string> = {};
              if (token) {
                headers["Authorization"] = `Bearer ${token}`;
              }

              const response = await fetch(apiUrl, { headers });
              const data = await response.json();

              if (!response.ok) {
                throw new Error(data.error || "Failed to fetch guest token");
              }

              return data.token;
            } catch (error) {
              console.error("[Reporting] Error fetching guest token:", error);
              throw error;
            }
          },
          dashboardUiConfig: {
            hideTitle: false,
            hideTab: false,
            hideChartControls: true,
            filters: {
              expanded: true,
            },
          },
        });

        // Resize the iframe to fill container
        const iframeSuperset = dashboardRef.current
          .children[0] as HTMLIFrameElement;
        if (iframeSuperset) {
          iframeSuperset.style.width = "100%";
          iframeSuperset.style.height = "100vh"; // full viewport height
          iframeSuperset.style.border = "none";
        }
      } catch (err) {
        console.error("Failed to embed dashboard:", err);
        setError(
          "Unable to load the dashboard. The Superset guest token feature needs to be enabled. Please contact your administrator."
        );
      }
    }

    const timeout = setTimeout(embed, 50);
    return () => clearTimeout(timeout);
  }, [tenantId]);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100vh",
        width: "100vw",
        margin: 0,
        overflow: "hidden",
      }}
    >
      {/* Top Navbar */}
      <TopNavbar />

      {/* No tenant state */}
      {!tenantId && (
        <div
          style={{
            padding: "2rem",
            margin: "2rem",
            backgroundColor: "#f0f0f0",
            borderRadius: "8px",
            textAlign: "center",
          }}
        >
          <p>
            No tenant context available. Reporting requires a tenant to be
            selected.
          </p>
        </div>
      )}

      {/* Error message */}
      {error && (
        <div
          style={{
            padding: "2rem",
            margin: "2rem",
            backgroundColor: "#fee",
            border: "1px solid #fcc",
            borderRadius: "8px",
            color: "#c00",
          }}
        >
          <h3 style={{ marginTop: 0 }}>Dashboard Error</h3>
          <p>{error}</p>
          <details style={{ marginTop: "1rem" }}>
            <summary style={{ cursor: "pointer", fontWeight: "bold" }}>
              Technical Details
            </summary>
            <p style={{ marginTop: "0.5rem", fontSize: "0.9em" }}>
              The Superset server is returning a 403 Forbidden error when
              attempting to generate a guest token. This typically means the
              embedded dashboard feature is not enabled in Superset.
            </p>
            <p style={{ fontSize: "0.9em" }}>
              <strong>Required Superset Configuration:</strong>
            </p>
            <ul style={{ fontSize: "0.9em" }}>
              <li>FEATURE_FLAGS["EMBEDDED_SUPERSET"] = True</li>
              <li>GUEST_TOKEN_JWT_SECRET must be configured</li>
              <li>A "Public" role must exist with dashboard access</li>
              <li>Admin user needs "can grant guest token" permission</li>
            </ul>
          </details>
        </div>
      )}

      {/* Dashboard container */}
      <div
        id="dashboard-container"
        ref={dashboardRef}
        style={{
          width: "100%",
          height: "100vh",
          flexGrow: 1,
          minHeight: 0,
          display: error ? "none" : "block",
        }}
      />
    </div>
  );
};
